// Lecture d'un CSV (séparateur , ; ou tabulation, guillemets) et repérage des
// colonnes par leur en-tête. L'utilisateur peut corriger le mappage à l'écran.

export const FIELDS = ["first_name", "last_name", "phone", "email", "sku", "zone", "sector", "external_ref", "received_at", "notes"] as const;
export type Field = (typeof FIELDS)[number];
export const FIELD_LABEL: Record<Field, string> = {
  first_name: "Prénom", last_name: "Nom", phone: "Téléphone", email: "Email", sku: "Produit (référence)", zone: "Zone / ville", sector: "Secteur",
  external_ref: "Référence externe", received_at: "Date de réception", notes: "Notes",
};
const GUESS: Record<Field, RegExp> = {
  first_name: /^(pr[ée]nom|first ?name|firstname)$/i, last_name: /^(nom|last ?name|lastname|name)$/i, phone: /^(t[ée]l[ée]phone|tel|phone|mobile|portable|num[ée]ro)$/i,
  email: /^(e-?mail|courriel)$/i, sku: /^(produit|product|sku|r[ée]f[ée]rence produit|type)$/i, zone: /^(zone|ville|city|d[ée]partement|code postal|cp|r[ée]gion)$/i,
  sector: /^(secteur|m[ée]tier|sector)$/i, external_ref: /^(id|ref|r[ée]f[ée]rence|external_?ref|lead ?id)$/i, received_at: /^(date|re[çc]u le|received|created|cr[ée][ée] le)$/i, notes: /^(notes?|message|commentaire|besoin|description)$/i,
};

export function parseCsv(text: string): { headers: string[]; rows: string[][] } {
  const clean = text.replace(/^\uFEFF/, "");
  const firstLine = clean.split(/\r?\n/, 1)[0] ?? "";
  const sep = [";", ",", "\t"].map((c) => [c, (firstLine.match(new RegExp(`\\${c}`, "g")) ?? []).length] as const).sort((a, b) => b[1] - a[1])[0]![0];
  const rows: string[][] = []; let row: string[] = []; let cell = ""; let quoted = false;
  for (let i = 0; i < clean.length; i++) {
    const ch = clean[i]!;
    if (quoted) {
      if (ch === '"') { if (clean[i + 1] === '"') { cell += '"'; i++; } else quoted = false; } else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === sep) { row.push(cell); cell = ""; }
    else if (ch === "\n" || ch === "\r") { if (ch === "\r" && clean[i + 1] === "\n") i++; row.push(cell); cell = ""; if (row.some((c) => c.trim() !== "")) rows.push(row); row = []; }
    else cell += ch;
  }
  row.push(cell); if (row.some((c) => c.trim() !== "")) rows.push(row);
  const headers = (rows.shift() ?? []).map((h) => h.trim());
  return { headers, rows };
}

export function guessMapping(headers: string[]): Partial<Record<Field, number>> {
  const m: Partial<Record<Field, number>> = {};
  headers.forEach((h, i) => { for (const f of FIELDS) if (m[f] === undefined && GUESS[f].test(h.trim())) { m[f] = i; break; } });
  return m;
}

export function rowsToLeads(rows: string[][], mapping: Partial<Record<Field, number>>, headers: string[]) {
  return rows.map((r, idx) => {
    const get = (f: Field) => (mapping[f] === undefined ? "" : (r[mapping[f]!] ?? "").trim());
    // Tout ce qui n'est pas mappé est conservé dans le contenu du lead.
    const mapped = new Set(Object.values(mapping));
    const payload: Record<string, string> = {};
    headers.forEach((h, i) => { if (!mapped.has(i) && r[i]?.trim()) payload[h] = r[i]!.trim(); });
    if (get("notes")) payload.notes = get("notes");
    return {
      first_name: get("first_name"), last_name: get("last_name"), phone: get("phone"), email: get("email"), sku: get("sku"), zone: get("zone"), sector: get("sector"),
      external_ref: get("external_ref") || `ligne-${idx + 2}`, received_at: get("received_at") || null, notes: get("notes") || null, payload,
    };
  });
}
